import React from 'react';
import { useTranslation } from 'react-i18next';
import { Space, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { RiskSymbolCell } from '@client/components/ui/table/TableCells';
import { HtmlContent } from '@client/utils/html_content';
import { BiologicalLegend } from '@client/components/ui/productLegends';
import RiskIndexDisplay from '@client/components/ui/RiskIndexDisplay';
import deleteIcon from '../../../../img/client/icons/delete.svg';
import duplicateIcon from '../../../../img/client/icons/duplicate.svg';
import editIcon from '../../../../img/client/icons/edit.svg';
import documentIcon from '../../../../img/client/icons/document.svg';
import prescriptionEditIcon from '../../../../img/edit-icon.svg';
import { buildLabelUrl } from '../../../../client/utils/urlUtils';

/**
 * Column header with an info tooltip (infobulle) to the right of the title.
 * Reused across SP-442 rows 12 / 16 / 19 / 26.
 */
const HeaderWithInfo = ({ title, tooltip }) => (
  <span className="inline-flex items-center justify-center gap-1">
    <span className="text-[10px]">{title}</span>
    <Tooltip title={tooltip}>
      <InfoCircleOutlined className="text-gray-400 cursor-help text-[12px]" />
    </Tooltip>
  </span>
);

/**
 * Group header with a superscript footnote marker whose hover shows an
 * explanatory note. SP-442 rows 34/35 (« Risques pour la santé¹ ») and
 * 59/60 (« Risques pour l'environnement² »).
 */
const HeaderWithSuperscript = ({ title, sup, tooltip }) => (
  <span className="inline-flex items-start justify-center">
    <span>{title}</span>
    <Tooltip title={tooltip}>
      <sup className="text-[9px] ml-0.5 cursor-help">{sup}</sup>
    </Tooltip>
  </span>
);

/**
 * Risk-symbol cell for the calculator:
 * - SP-442 row 46 — leave the cell empty (not "N/A") when no symbol shows.
 * - SP-442 rows 50/51 — superscript "a" + professional-attribution note when a
 *   symbol is expert-assigned (only health symbols can be).
 * - SP-442 row 52 — "Critères limitants :" label for the limiting criteria.
 */
const SymbolCell = (props) => {
  const { t } = useTranslation('riskCalculator');
  return (
    <RiskSymbolCell
      {...props}
      hideWhenEmpty
      expertMarker="a"
      professionalTooltip={t('risks_professional_attribution')}
      criteriaLabel={t('risks_limiting_criteria')}
    />
  );
};

/**
 * Culture cell — name shown in the language selected on the Cultures step
 * (SP-442 row 13). Hovering the name reveals the culture height (SP-442 row 14),
 * which influences the environmental risk indices.
 */
const CultureCell = ({ culture, heightLabel }) => {
  // Latin culture names carry rich-text markup (italics / HTML entities), so
  // render via HtmlContent rather than as plain text.
  const nameEl = <HtmlContent html={culture?.name || '-'} className="inline" />;
  if (!culture?.height) return <span>{nameEl}</span>;
  return (
    <Tooltip title={`${heightLabel} : ${culture.height}`}>
      <span className="cursor-help underline decoration-dotted underline-offset-2">
        {nameEl}
      </span>
    </Tooltip>
  );
};

/**
 * Application-technique cell — shows the abbreviation (SP-442 row 17); hovering
 * reveals the full « Technique ou lieu d'application » name (SP-442 row 18).
 * Falls back to the full name when no abbreviation is configured.
 */
const TechniqueCell = ({ technique }) => {
  const abbr = technique?.abbreviation;
  const name = technique?.name;
  if (!abbr) return <span>{name || '-'}</span>;
  return (
    <Tooltip title={name}>
      <span className="cursor-help underline decoration-dotted underline-offset-2">
        {abbr}
      </span>
    </Tooltip>
  );
};

/**
 * Action button component with circular icon style
 * Matches the design from Figma
 */
const ActionButton = ({ icon, alt, tooltip, onClick }) => (
  <Tooltip title={tooltip}>
    <button
      onClick={onClick}
      className="flex w-8 h-8 items-center justify-center rounded-full border cursor-pointer bg-white hover:bg-gray-50"
      style={{ border: '1px solid #7CB305' }}
    >
      <img
        src={icon}
        alt={alt}
        width="16"
        height="16"
        className="pointer-events-none"
      />
    </button>
  </Tooltip>
);

/**
 * Product cell component - displays product name, registration number, and active substances
 * Matches Figma design tokens exactly:
 * - Product name: color #105235, font-weight 700, font-size 12px
 * - Registration number: color #000, font-weight 400, font-size 12px
 * - Active substances: color #105235, font-weight 400, font-size 12px
 */
const ProductCell = ({ products, t }) => {
  if (!products || products.length === 0) return <span>-</span>;

  return (
    <div>
      {products.map((product, index) => {
        const productTypeNames = product.productTypes
          ?.map((pt) => pt?.name)
          .filter(Boolean)
          .join(', ');

        const substances = product.activeSubstances || [];

        // SP-442 row 23 — hovering the product name / registration number lists
        // the active substances (rich text, rendered via HtmlContent).
        const substancesHover =
          substances.length > 0 ? (
            <div className="text-xs">
              <div className="font-semibold mb-0.5">
                {t('col_active_substances')} :
              </div>
              <div>
                {substances.map((s, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && ' / '}
                    <HtmlContent html={s} className="inline" />
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : null;

        const nameInner = (
          <span className="text-xs leading-[125%] font-roboto">
            {/* SP-442 row 24 — clicking the product name opens its Fiche
                produit commercial in a new tab. */}
            <a
              href={`/produits-commerciaux/details/${product.id}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#105235] font-bold hover:underline hover:text-[#105235]"
            >
              {product.name}
            </a>
            {product.approvalNumber && (
              <span className="text-black font-normal">
                {' '}
                ({product.approvalNumber})
              </span>
            )}
          </span>
        );

        return (
          <React.Fragment key={product.id || index}>
            {/* Plus sign separator for multiple products */}
            {index > 0 && (
              <div className="flex justify-center py-1">
                <div className="w-4 h-4 flex justify-center items-center rounded-full bg-[#E9F1D9] text-black text-xs font-medium">
                  +
                </div>
              </div>
            )}

            {/* Name + registration number (hover → active substances) followed
                by SP-442 row 22 Bio / Prescription agronomique badges. */}
            <div className="flex flex-wrap justify-center items-center gap-1">
              {substancesHover ? (
                <Tooltip title={substancesHover}>
                  <span className="cursor-help">{nameInner}</span>
                </Tooltip>
              ) : (
                nameInner
              )}

              {product.isOrganic && (
                <Tooltip title={t('common|legends.biologicalApprovalTooltip')}>
                  <span className="inline-flex items-center">
                    <BiologicalLegend height={16} />
                  </span>
                </Tooltip>
              )}

              {product.hasPrescription && (
                <Tooltip
                  title={
                    product.prescriptionComment ? (
                      <HtmlContent html={product.prescriptionComment} />
                    ) : (
                      t('product_prescription_label')
                    )
                  }
                >
                  {/* Prescription agronomique — same red box + pencil étiquette
                      as the legend (SP-442 row 22). */}
                  <span className="inline-flex h-[18px] w-[18px] justify-center items-center rounded bg-[#FF4D4F] cursor-default">
                    <img
                      src={prescriptionEditIcon}
                      alt={t('product_prescription_label')}
                      className="w-3 h-3 pointer-events-none"
                    />
                  </span>
                </Tooltip>
              )}
            </div>

            {/* Product types */}
            {productTypeNames && (
              <div className="text-black font-normal text-xs leading-[125%] font-roboto">
                ({productTypeNames})
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * Label/Étiquette cell component - document icon that links to product label
 * Aligns with ProductCell - uses same spacing pattern as the "+" separator
 */
const LabelCell = ({ products, tooltip }) => {
  if (!products || products.length === 0) return <span>-</span>;

  const handleViewLabel = (approvalNumber) => {
    if (approvalNumber) {
      window.open(
        buildLabelUrl(approvalNumber),
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  return (
    <div>
      {products.map((product, index) => (
        <React.Fragment key={product.id || index}>
          {/* Separator space aligned with "+" in ProductCell */}
          {index > 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '4px 0',
                height: '20px',
              }}
            >
              {/* Empty space to align with "+" separator */}
            </div>
          )}

          {/* Document icon - clickable to open product label */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '32px',
            }}
          >
            {product.approvalNumber ? (
              <Tooltip title={tooltip}>
                <button
                  onClick={() => handleViewLabel(product.approvalNumber)}
                  className="flex items-center justify-center rounded-lg border border-[#D9D9D9] bg-white cursor-pointer transition-all hover:border-[#7CB305] hover:bg-[#F6FFED]"
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '8px',
                    border: '1px solid #D9D9D9',
                    background: '#fff',
                  }}
                >
                  <img
                    src={documentIcon}
                    alt="Étiquette"
                    width="20"
                    height="20"
                    className="pointer-events-none"
                  />
                </button>
              </Tooltip>
            ) : (
              <span style={{ color: '#8C8C8C' }}>-</span>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Applied dose cell component - displays dose and unit for each product
 * Aligns with ProductCell - uses same spacing pattern as the "+" separator
 */
const AppliedDoseCell = ({ products, t }) => {
  if (!products || products.length === 0) return <span>-</span>;

  return (
    <div>
      {products.map((product, index) => {
        const dosages = product.listedDosages
          ? String(product.listedDosages).split(' • ').filter(Boolean)
          : [];

        // SP-442 row 30 — hovering the dose lists the dosages recorded in
        // SAgE pesticides (the data formerly shown in its own column).
        const doseHover =
          dosages.length > 0 ? (
            <div className="text-xs">
              <div className="font-semibold mb-0.5">
                {t('dose_listed_dosages_title')}
              </div>
              {dosages.map((d, i) => (
                <div key={i}>{d}</div>
              ))}
            </div>
          ) : null;

        const doseContent = (
          <div className="min-h-[32px] flex items-center text-xs">
            {product.dose} {product.unit?.name || ''}
          </div>
        );

        return (
          <React.Fragment key={product.id || index}>
            {/* Line separator between products */}
            {index > 0 && <div className="h-[1px] bg-[#BFBFBF] my-2" />}

            {doseHover ? (
              <Tooltip title={doseHover}>
                <span className="cursor-help">{doseContent}</span>
              </Tooltip>
            ) : (
              doseContent
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * Risk index cell component (IRS/IRE) - styled box with value
 * Figma design: 32x32px box, rounded corners, neutral background
 */
const RiskIndexCell = ({ value }) => {
  if (value === null || value === undefined) return <span>-</span>;

  return (
    <div className="px-2 py-1 flex justify-center items-center gap-2 rounded-[8px] bg-[#F5F5F5] text-black font-roboto text-xs font-medium leading-[125%]">
      {value}
    </div>
  );
};

/**
 * Factory function for creating table columns for risk calculator results
 * Matches Figma design pixel-perfect
 *
 * @param {Function} t - Translation function
 * @param {Object} handlers - Action handlers (onEdit, onDuplicate, onDelete)
 * @param {boolean} readOnly - Whether to hide the actions column
 */
export const createTableColumns = (t, handlers = {}, readOnly = false) => {
  const columns = [];

  // Only add actions column if not in read-only mode
  if (!readOnly) {
    columns.push({
      title: <div className="vertical-header-text">{t('col_actions')}</div>,
      key: 'actions',
      fixed: 'left',
      width: 50,
      align: 'center',
      className: 'vertical-cell',
      render: (_, record) => (
        <Space
          direction="vertical"
          size={4}
          align="center"
          style={{ width: '100%' }}
        >
          <ActionButton
            icon={duplicateIcon}
            alt={t('action_duplicate')}
            tooltip={t('action_duplicate')}
            onClick={() => handlers.onDuplicate?.(record)}
          />
          <ActionButton
            icon={editIcon}
            alt={t('action_edit')}
            tooltip={t('action_edit')}
            onClick={() => handlers.onEdit?.(record)}
          />
          <ActionButton
            icon={deleteIcon}
            alt={t('action_delete')}
            tooltip={t('action_delete')}
            onClick={() => handlers.onDelete?.(record)}
          />
        </Space>
      ),
    });
  }

  // Add remaining columns
  columns.push(
    {
      title: (
        <HeaderWithInfo
          title={t('col_culture')}
          tooltip={t('col_culture_tooltip')}
        />
      ),
      className: 'text-center',
      key: 'culture',
      align: 'center',
      width: 110,
      render: (_, record) => (
        <CultureCell
          culture={record.culture}
          heightLabel={t('culture_height_label')}
        />
      ),
    },
    {
      title: <span className="text-[10px]">{t('col_treatment_type')}</span>,
      dataIndex: ['treatmentType', 'name'],
      key: 'treatmentType',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <HeaderWithInfo
          title={t('col_application_technique')}
          tooltip={t('col_application_technique_tooltip')}
        />
      ),
      key: 'applicationTechnique',
      align: 'center',
      width: 95,
      render: (_, record) => (
        <TechniqueCell technique={record.applicationTechnique} />
      ),
    },
    {
      title: (
        <div>
          {/* SP-442 row 19 — infobulle to the right of « Produits commerciaux ». */}
          <HeaderWithInfo
            title={t('col_commercial_products')}
            tooltip={t('col_commercial_products_tooltip')}
          />
          <div className="text-[10px] font-normal">
            {t('col_active_substances')}
          </div>
        </div>
      ),
      key: 'products_and_substances',
      align: 'center',
      width: 120,
      render: (_, record) => <ProductCell products={record.products} t={t} />,
    },
    {
      // SP-442 (row 8): « Dose appliquée » now precedes « Étiquette ».
      title: (
        <div>
          {/* SP-442 row 26 — infobulle to the right of « Dose appliquée ». */}
          <HeaderWithInfo
            title={t('col_applied_dose')}
            tooltip={t('col_applied_dose_tooltip')}
          />
          <div className="text-[10px] font-normal">{t('col_unit')}</div>
        </div>
      ),
      key: 'applied_dose',
      align: 'center',
      width: 90,
      render: (_, record) => (
        <AppliedDoseCell products={record.products} t={t} />
      ),
    },
    {
      title: <div className="vertical-header-text">{t('col_labels')}</div>,
      key: 'labels',
      width: 45,
      align: 'center',
      className: 'vertical-cell',
      render: (_, record) => (
        <LabelCell
          products={record.products}
          tooltip={t('products_label_tooltip')}
        />
      ),
    },
    {
      // SP-442 rows 34/35 — « Risques pour la santé » with a superscript "1"
      // footnote whose hover shows the biopesticide-methodology note.
      title: (
        <div className="py-[10px]">
          <HeaderWithSuperscript
            title={t('col_risks_health')}
            sup="1"
            tooltip={t('risks_footnote_biopesticide')}
          />
        </div>
      ),
      children: [
        {
          title: <div className="vertical-header-text">{t('col_irs')}</div>,
          key: 'health_risk_index',
          className: 'vertical-cell risk-irs-ire',
          width: 54,
          align: 'center',
          // SP-442 row 36 — hide the whole health block when not publicly visible.
          render: (_, record) =>
            record.results?.health_risk_visible === false ? null : (
              // SP-442 rows 40/41 — expert-assigned IRS shows "††" + biopesticide note.
              <RiskIndexDisplay
                value={record.results?.health_risk_index}
                isExpert={record.results?.health_risk_index_is_expert}
                expertTooltip={t('risks_footnote_biopesticide')}
                variant="badge"
                className="!px-1 whitespace-nowrap"
              />
            ),
        },
        {
          title: (
            <div className="vertical-header-text">
              {t('col_acute_toxicity')}
            </div>
          ),
          key: 'acute_toxicity_symbol',
          className: 'vertical-cell',
          render: (_, record) =>
            record.results?.health_risk_visible === false ? null : (
              <SymbolCell
                symbolData={record.symbols?.health?.acute_toxicity_symbol}
                alt={t('col_acute_toxicity')}
              />
            ),
          width: 44,
          align: 'center',
        },
        {
          title: (
            <div className="vertical-header-text">
              {t('col_long_term_effects')}
            </div>
          ),
          key: 'long_term_effect_symbol',
          className: 'vertical-cell',
          render: (_, record) =>
            record.results?.health_risk_visible === false ? null : (
              <SymbolCell
                symbolData={record.symbols?.health?.long_term_effect_symbol}
                alt={t('col_long_term_effects')}
              />
            ),
          width: 44,
          align: 'center',
        },
      ],
    },
    {
      // SP-442 rows 59/60 — « Risques pour l'environnement » with a superscript
      // "2" footnote whose hover shows the biopesticide-methodology note.
      title: (
        <div className="py-[10px]">
          <HeaderWithSuperscript
            title={t('col_risks_environment')}
            sup="2"
            tooltip={t('risks_footnote_biopesticide')}
          />
        </div>
      ),
      children: [
        {
          title: <div className="vertical-header-text">{t('col_ire')}</div>,
          key: 'environment_risk_index',
          className: 'vertical-cell risk-irs-ire',
          width: 54,
          align: 'center',
          // SP-442 row 61 — hide the whole environment block when not visible.
          render: (_, record) =>
            record.results?.environment_risk_visible === false ? null : (
              // SP-442 rows 65/66 — expert-assigned IRE shows "††" + biopesticide note.
              <RiskIndexDisplay
                value={record.results?.environment_risk_index}
                isExpert={record.results?.environment_risk_index_is_expert}
                expertTooltip={t('risks_footnote_biopesticide')}
                variant="badge"
                className="!px-1 whitespace-nowrap"
              />
            ),
        },
        {
          title: <div className="vertical-header-text">{t('col_fish')}</div>,
          key: 'fishes_symbol',
          className: 'vertical-cell',
          render: (_, record) =>
            record.results?.environment_risk_visible === false ? null : (
              <SymbolCell
                symbolData={record.symbols?.environment?.fishes_symbol}
                alt={t('col_fish')}
                nameOverride={t('symbol_name_aquatic_organisms')}
              />
            ),
          width: 44,
          align: 'center',
        },
        {
          title: <div className="vertical-header-text">{t('col_birds')}</div>,
          key: 'birds_symbol',
          className: 'vertical-cell',
          render: (_, record) =>
            record.results?.environment_risk_visible === false ? null : (
              <SymbolCell
                symbolData={record.symbols?.environment?.birds_symbol}
                alt={t('col_birds')}
              />
            ),
          width: 44,
          align: 'center',
        },
        {
          title: <div className="vertical-header-text">{t('col_bees')}</div>,
          key: 'bees_symbol',
          className: 'vertical-cell',
          render: (_, record) =>
            record.results?.environment_risk_visible === false ? null : (
              <SymbolCell
                symbolData={record.symbols?.environment?.bees_symbol}
                alt={t('col_bees')}
              />
            ),
          width: 44,
          align: 'center',
        },
        {
          title: (
            <div className="vertical-header-text">{t('col_persistence')}</div>
          ),
          key: 'persistence_symbol',
          className: 'vertical-cell',
          render: (_, record) =>
            record.results?.environment_risk_visible === false ? null : (
              <SymbolCell
                symbolData={record.symbols?.environment?.persistence_symbol}
                alt={t('col_persistence')}
              />
            ),
          width: 44,
          align: 'center',
        },
        {
          title: (
            <div className="vertical-header-text">
              {t('symbol_name_mobility')}
            </div>
          ),
          key: 'leaching_symbol',
          className: 'vertical-cell',
          render: (_, record) =>
            record.results?.environment_risk_visible === false ? null : (
              <SymbolCell
                symbolData={record.symbols?.environment?.leaching_symbol}
                alt={t('symbol_name_mobility')}
                nameOverride={t('symbol_name_mobility')}
              />
            ),
          width: 44,
          align: 'center',
        },
      ],
    }
  );

  return columns;
};
